import { CompType } from '@aloft/toolkit-shared';
import { colors } from '@aloft/toolkit-sui';
import { Image, Text, View } from '@react-pdf/renderer';

import { SALES_COMPARISON_ROWS_PER_PAGE } from '../constants.ts';
import { fontStyles, salesComparisonPageStyles as styles } from '../styles.ts';
import {
  SalesComparisonAdjustmentKey,
  SalesComparisonGridColumnData,
  SalesComparisonRows,
} from '../types.ts';
import { splitSalesComparisonRows } from '../utils/splitSalesComparisonRows.ts';
import { CompTypeBadge } from './CompTypeBadge.tsx';
import { ImageOffLineSvg } from './ImageOffLineSvg.tsx';
import { SalesComparisonGlossary } from './SalesComparisonGlossary.tsx';
import { SalesComparisonRow } from './SalesComparisonRow.tsx';
import { Tkv2PDFPage } from './Tkv2PDFPage.tsx';

const renderRows = (
  rows: SalesComparisonRows,
  subject: SalesComparisonGridColumnData,
  chunk: SalesComparisonGridColumnData[],
) =>
  rows.map((field) => (
    <SalesComparisonRow
      key={`scc-field-${field.key[0]}`}
      fields={field.key}
      labels={field.label}
      subject={subject}
      comparables={chunk}
      adjustment={field.adjustment as SalesComparisonAdjustmentKey}
      borderTop={field.borderTop}
      bold={field.bold}
      align={(field.align as 'left' | 'right' | 'center') || 'left'}
    />
  ));

const CompPhotoHeader = ({
  subject,
  chunk,
  compType,
}: {
  subject: SalesComparisonGridColumnData;
  chunk: SalesComparisonGridColumnData[];
  compType?: CompType;
}) => (
  <>
    <View style={styles.row}>
      {[subject, ...chunk].map((comp, i) => (
        <View style={styles.gridColumn} key={`scc-card-${comp?.address}`}>
          <View style={compType ? [styles.row, styles.rowSpaceBetween] : undefined}>
            <Text style={styles.header}>{i === 0 ? 'SUBJECT' : `COMP ${comp?.position}`}</Text>
            {compType && <CompTypeBadge compType={compType} fullText compact />}
          </View>
          <View style={styles.imageContainer}>
            {comp?.imageUrl ? (
              <Image src={comp.imageUrl} style={styles.image} />
            ) : (
              <View style={{ justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <ImageOffLineSvg width={24} height={24} />
              </View>
            )}
          </View>
        </View>
      ))}
    </View>

    <View style={styles.row}>
      {[subject, ...chunk].map((comp) => (
        <View
          style={{ ...styles.gridColumn, ...styles.addressContainer }}
          key={`scc-address-${comp?.address}`}
        >
          <Text style={styles.address}>{comp?.address}</Text>
        </View>
      ))}
    </View>
  </>
);

export const SalesComparisonTablePages = ({
  subjectAddress,
  subject,
  chunk,
  filteredRows,
  pageIndex,
  pageKeyPrefix,
  compType,
  badge,
  opinionOfValue,
}: {
  subjectAddress: string;
  subject: SalesComparisonGridColumnData;
  chunk: SalesComparisonGridColumnData[];
  filteredRows: SalesComparisonRows;
  pageIndex: number;
  pageKeyPrefix: string;
  compType?: CompType;
  badge?: CompType;
  opinionOfValue?: string;
}) => {
  const { firstPageRows, continuationRows } = splitSalesComparisonRows(
    filteredRows,
    SALES_COMPARISON_ROWS_PER_PAGE,
  );
  const properties = [subject, ...chunk];
  const hasContinuation = continuationRows.length > 0;

  const getCompTypeColor = (compType?: CompType) => {
    switch (compType) {
      case 'aiv':
        return colors.aivLight;
      case 'arv':
        return colors.arvLight;
      default:
        return 'transparent';
    }
  };

  return (
    <>
      <Tkv2PDFPage
        key={`${pageKeyPrefix}-${pageIndex}-part-1`}
        title="Sales Comparison Approach"
        address={subjectAddress}
        badge={badge}
      >
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignSelf: 'flex-start',
            backgroundColor: getCompTypeColor(compType),
            paddingHorizontal: 4,
          }}
        >
          <Text style={fontStyles.bodyRegularLight}>
            Opinion of {compType === 'arv' ? 'After Repair Value' : 'Market Value'}:{' '}
          </Text>
          <Text style={fontStyles.body}>{opinionOfValue}</Text>
        </View>
        <View style={styles.content}>
          <CompPhotoHeader subject={subject} chunk={chunk} compType={compType} />
          {renderRows(firstPageRows, subject, chunk)}
          {!hasContinuation && (
            <SalesComparisonGlossary filteredRows={filteredRows} properties={properties} />
          )}
        </View>
      </Tkv2PDFPage>

      {hasContinuation && (
        <Tkv2PDFPage
          key={`${pageKeyPrefix}-${pageIndex}-part-2`}
          title="Sales Comparison Approach"
          address={subjectAddress}
          badge={badge}
        >
          <View style={styles.content}>
            {renderRows(continuationRows, subject, chunk)}
            <SalesComparisonGlossary filteredRows={filteredRows} properties={properties} />
          </View>
        </Tkv2PDFPage>
      )}
    </>
  );
};
